import {
  chroma,
  contrastRatio,
  ensureContrast,
  lightness,
  mix,
  normalizeOpaqueHex,
  withAlpha,
} from './color';

export type AppearanceVariant = 'light' | 'dark';

/**
 * What a theme changes in the app's chrome: three colors and a contrast, from which every
 * surface, text and line color is derived — so any theme stays as calm and legible as the
 * default one. Fonts are optional; semantic colors tint diffs and agent activity.
 */
export type ChromeTheme = Readonly<{
  accent: string;
  ink: string;
  surface: string;
  /** 0 to 100: how far surfaces, lines and secondary text step away from the background. */
  contrast: number;
  fonts: Readonly<{ ui: string | null; code: string | null }>;
  semanticColors: Readonly<{ diffAdded: string; diffRemoved: string; skill: string }>;
}>;

/**
 * The app's own palette, as `packages/ui/src/theme.css` defines it (the dark one is the charter:
 * content #1b1c20, text #f0f1f3, the filled button #ececef). A chrome equal to it adds no
 * stylesheet, so the charter is what shows unless the user makes a theme of their own.
 */
export const CROWDEX_CHROME: Readonly<Record<AppearanceVariant, ChromeTheme>> = {
  dark: {
    accent: '#ececef',
    ink: '#f0f1f3',
    surface: '#1b1c20',
    contrast: 60,
    fonts: { ui: null, code: null },
    semanticColors: { diffAdded: '#1cce79', diffRemoved: '#ff6467', skill: '#ac8ff8' },
  },
  light: {
    accent: '#202328',
    ink: '#15171b',
    surface: '#f6f7f9',
    contrast: 45,
    fonts: { ui: null, code: null },
    semanticColors: { diffAdded: '#00a240', diffRemoved: '#e7000b', skill: '#7c4ddb' },
  },
};

const FONT_NAME = /^[\p{L}\p{N} ._'-]{1,64}$/u;

/** A single font family name, or `null` (the platform's own font). */
export function normalizeFontName(value: unknown): string | null {
  if (typeof value !== 'string') return null;
  const name = value.trim().replace(/^["']|["']$/g, '');
  return FONT_NAME.test(name) ? name : null;
}

function clampContrast(value: unknown, fallback: number): number {
  return typeof value === 'number' && Number.isFinite(value)
    ? Math.round(Math.min(100, Math.max(0, value)))
    : fallback;
}

/**
 * The dark default before the charter. Settings saved then carry it as a chrome of their own;
 * read back, it is the default again, so the charter is not overridden by a copy of the old look.
 */
const PRE_CHARTER_DARK = { accent: '#e3e4e7', ink: '#edeef1', surface: '#16171a' } as const;

/** A stored or imported chrome theme, every invalid field replaced by the variant's default. */
export function normalizeChromeTheme(input: unknown, variant: AppearanceVariant): ChromeTheme {
  const theme = readChromeTheme(input, variant);
  if (variant !== 'dark') return theme;
  // Only the colours are compared: a user who changed a font keeps it, on the charter's colours.
  const { fonts, ...colours } = theme;
  const { fonts: _defaultFonts, ...charter } = CROWDEX_CHROME.dark;
  const preCharter = { ...charter, ...PRE_CHARTER_DARK };
  return JSON.stringify(colours) === JSON.stringify(preCharter)
    ? { ...CROWDEX_CHROME.dark, fonts }
    : theme;
}

function readChromeTheme(input: unknown, variant: AppearanceVariant): ChromeTheme {
  const base = CROWDEX_CHROME[variant];
  const source = (typeof input === 'object' && input !== null ? input : {}) as Record<
    string,
    unknown
  >;
  const color = (value: unknown, fallback: string) =>
    normalizeOpaqueHex(typeof value === 'string' ? value : null) ?? fallback;
  const fonts = (source['fonts'] ?? {}) as Record<string, unknown>;
  const semantic = (source['semanticColors'] ?? {}) as Record<string, unknown>;
  return {
    accent: color(source['accent'], base.accent),
    ink: color(source['ink'], base.ink),
    surface: color(source['surface'], base.surface),
    contrast: clampContrast(source['contrast'], base.contrast),
    fonts: { ui: normalizeFontName(fonts['ui']), code: normalizeFontName(fonts['code']) },
    semanticColors: {
      diffAdded: color(semantic['diffAdded'], base.semanticColors.diffAdded),
      diffRemoved: color(semantic['diffRemoved'], base.semanticColors.diffRemoved),
      skill: color(semantic['skill'], base.semanticColors.skill),
    },
  };
}

export function sameChromeTheme(a: ChromeTheme, b: ChromeTheme): boolean {
  return JSON.stringify(a) === JSON.stringify(b);
}

/**
 * How far each surface steps from the background at the variant's default contrast, calibrated
 * on the default palette: positive toward the ink, negative away from it (darker in dark,
 * lighter in light).
 */
const STEPS: Readonly<Record<AppearanceVariant, Readonly<Record<string, number>>>> = {
  // The charter: the bar darker than the content, one surface for cards and popups, one for
  // controls.
  dark: {
    sidebar: -0.145,
    chrome: -0.145,
    card: 0.061,
    elevated: 0.061,
    overlay: 0.061,
    muted: 0.117,
    secondary: 0.117,
    hover: 0.117,
    control: 0.117,
    mutedText: 0.68,
    subtleText: 0.585,
    bubble: 0.095,
    codeSurface: -0.106,
    border: 0.1,
    input: 0.14,
    hairline: 0.1,
  },
  light: {
    sidebar: 0.039,
    chrome: 0.078,
    card: -0.8,
    elevated: 0.058,
    overlay: -0.8,
    muted: 0.045,
    secondary: 0.045,
    hover: 0.065,
    control: 0.026,
    mutedText: 0.62,
    subtleText: 0.486,
    bubble: 0.058,
    codeSurface: 0.026,
    border: 0.13,
    input: 0.17,
    hairline: 0.12,
  },
};

const SANS_STACK = 'system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif';
const MONO_STACK = 'ui-monospace, "SF Mono", Menlo, Monaco, Consolas, monospace';

/**
 * The text color that reads on `fill`: the theme's surface or ink when either is legible on it,
 * otherwise white or black, whichever contrasts more.
 */
function onColor(fill: string, theme: ChromeTheme): string {
  const [best] = [theme.surface, theme.ink].sort(
    (a, b) => contrastRatio(b, fill) - contrastRatio(a, fill),
  ) as [string];
  if (contrastRatio(best, fill) >= 4.5) return best;
  return contrastRatio('#ffffff', fill) >= contrastRatio('#000000', fill) ? '#ffffff' : '#000000';
}

/**
 * The app tokens a chrome theme sets, as literal colors (no `var()`), so isolated plugin views
 * receive values they can use as they are.
 */
export function chromeTokens(
  theme: ChromeTheme,
  variant: AppearanceVariant,
): Record<string, string> {
  const { ink, surface, accent } = theme;
  const scale = theme.contrast / CROWDEX_CHROME[variant].contrast;
  const away = variant === 'dark' ? '#000000' : '#ffffff';
  const step = (name: string) => {
    const amount = (STEPS[variant][name] ?? 0) * scale;
    return amount >= 0 ? mix(surface, ink, amount) : mix(surface, away, -amount);
  };
  const line = (name: string) => withAlpha(ink, Math.min(1, (STEPS[variant][name] ?? 0) * scale));
  const borderAlpha = (STEPS[variant]['border'] ?? 0) * scale;
  const sidebar = step('sidebar');
  const card = step('card');
  const overlay = step('overlay');
  const muted = step('muted');
  const mutedText = ensureContrast(step('mutedText'), surface, ink, 3);
  // Tertiary text, held to the same floor as secondary text.
  const subtleText = ensureContrast(step('subtleText'), surface, ink, 3);
  const onAccent = onColor(accent, theme);
  // A colorless accent reads as plain text; links keep a distinct, legible tone.
  const reference = ensureContrast(
    chroma(accent) < 0.03 ? mix(ink, surface, 0.25) : accent,
    surface,
    ink,
    4.5,
  );
  const ring = mix(accent, surface, 0.3);
  const { diffAdded, diffRemoved, skill } = theme.semanticColors;
  const tokens: Record<string, string> = {
    '--background': surface,
    '--foreground': ink,
    '--card': card,
    '--card-foreground': ink,
    '--popover': overlay,
    '--popover-foreground': ink,
    '--primary': accent,
    '--primary-foreground': onAccent,
    '--secondary': step('secondary'),
    '--secondary-foreground': ink,
    '--muted': muted,
    '--muted-foreground': mutedText,
    '--subtle-foreground': subtleText,
    // My messages' bubble sits a little above the cards; code blocks a little below the content.
    '--bubble': step('bubble'),
    '--code-surface': step('codeSurface'),
    '--accent': step('hover'),
    '--accent-foreground': ink,
    '--destructive': diffRemoved,
    '--destructive-foreground': onColor(diffRemoved, theme),
    '--border': line('border'),
    '--input': line('input'),
    '--control': step('control'),
    '--ring': ring,
    '--reference': reference,
    '--sidebar': sidebar,
    '--sidebar-foreground': ink,
    '--sidebar-primary': accent,
    '--sidebar-primary-foreground': onAccent,
    '--sidebar-accent': step('hover'),
    '--sidebar-accent-foreground': ink,
    '--sidebar-border': line('hairline'),
    '--sidebar-ring': ring,
    '--desktop-sidebar': sidebar,
    '--desktop-chrome': step('chrome'),
    '--desktop-elevated': step('elevated'),
    '--desktop-overlay': overlay,
    '--desktop-hairline': line('hairline'),
    '--desktop-success': diffAdded,
    '--desktop-agent': skill,
    // Re-declared here so they resolve against this theme's card and border, not the root's.
    '--surface-sunken': withAlpha(card, 0.35),
    '--surface-panel': withAlpha(card, 0.45),
    '--surface-raised': withAlpha(card, 0.7),
    '--surface-overlay': withAlpha(card, 0.85),
    '--surface-solid': withAlpha(card, 0.95),
    '--hairline-faint': withAlpha(ink, Math.min(1, borderAlpha * 0.5)),
    '--hairline': withAlpha(ink, Math.min(1, borderAlpha * 0.7)),
    '--hairline-strong': withAlpha(ink, Math.min(1, borderAlpha * 0.8)),
    // The design system's components (src/crow) draw with the Crow tokens: the same theme.
    '--crow-bg': surface,
    '--crow-surface': card,
    '--crow-raised': step('control'),
    '--crow-line': line('hairline'),
    '--crow-line-strong': line('border'),
    '--crow-text': ink,
    '--crow-text-2': mutedText,
    '--crow-text-3': subtleText,
    '--crow-input': line('input'),
    '--crow-accent': accent,
    '--crow-on-accent': onAccent,
    '--crow-accent-ink': reference,
    '--crow-danger': diffRemoved,
    '--crow-on-danger': onColor(diffRemoved, theme),
    '--crow-success': diffAdded,
    '--crow-focus-ring': ring,
  };
  if (theme.fonts.ui) tokens['--font-sans-value'] = `"${theme.fonts.ui}", ${SANS_STACK}`;
  if (theme.fonts.code) tokens['--font-mono-value'] = `"${theme.fonts.code}", ${MONO_STACK}`;
  return tokens;
}

/** Whether a dark ink on a light surface (or the reverse) matches the variant it is used for. */
export function chromeMatchesVariant(theme: ChromeTheme, variant: AppearanceVariant): boolean {
  return variant === 'dark'
    ? lightness(theme.surface) < lightness(theme.ink)
    : lightness(theme.surface) > lightness(theme.ink);
}
